
/**
 * Security utilities for application protection
 */

// Function to validate password strength
export const validatePasswordStrength = (password: string): { valid: boolean; message: string } => {
  if (password.length < 8) {
    return { valid: false, message: 'Password must be at least 8 characters long' };
  }
  
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
  
  const strength = [hasUpperCase, hasLowerCase, hasNumbers, hasSpecialChar].filter(Boolean).length;
  
  if (strength < 3) {
    return { 
      valid: false, 
      message: 'Password must contain at least 3 of the following: uppercase letters, lowercase letters, numbers, and special characters' 
    };
  }
  
  return { valid: true, message: 'Password is strong' };
};

// Rate limiting implementation
const rateLimits: { [key: string]: { count: number; timestamp: number } } = {};

export const checkRateLimit = (key: string, limit: number, windowMs: number): boolean => {
  const now = Date.now();
  const windowStart = now - windowMs;
  
  // Clean up expired entries
  Object.keys(rateLimits).forEach(k => {
    if (rateLimits[k].timestamp < windowStart) {
      delete rateLimits[k];
    }
  });
  
  if (!rateLimits[key]) {
    rateLimits[key] = { count: 1, timestamp: now };
    return true;
  }
  
  if (rateLimits[key].count >= limit) {
    return false;
  }
  
  rateLimits[key].count += 1;
  rateLimits[key].timestamp = now;
  return true;
};

// Email validation
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

// Phone validation
export const isValidPhoneNumber = (phone: string): boolean => {
  // Basic format check - can be expanded based on requirements
  const phoneRegex = /^\+?[0-9]{8,15}$/;
  return phoneRegex.test(phone);
};

// CSRF token generation
export const generateCSRFToken = (): string => {
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

// Store CSRF token
export const storeCSRFToken = (token: string): void => {
  sessionStorage.setItem('csrfToken', token);
};

// Get stored CSRF token
export const getCSRFToken = (): string | null => {
  return sessionStorage.getItem('csrfToken');
};

// Admin panel password verification
export const verifyAdminPassword = (password: string): boolean => {
  // Hardcoded password: dnADip$2025
  return password === 'dnADip$2025';
};

// Check if admin is authenticated
export const isAdminAuthenticated = (): boolean => {
  return sessionStorage.getItem('adminAuthenticated') === 'true';
};

// Set admin authentication status
export const setAdminAuthenticated = (status: boolean): void => {
  if (status) {
    sessionStorage.setItem('adminAuthenticated', 'true');
  } else {
    sessionStorage.removeItem('adminAuthenticated');
  }
};
